/**
 * The core mobile receiving/scanning screen (spec §9-§10). Hardware
 * barcode scanners on the target Android devices emulate a keyboard —
 * they type the barcode digits then send Enter — so the whole screen is
 * built around one persistently-focused text input that submits on
 * Enter, with everything else (undo, manual edit, exceptions) reachable
 * without ever needing to tap into the input itself.
 */
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { api, ApiError, ApiNetworkError } from '../api.js';
import { escapeHtml, newCorrelationId, qs } from '../dom.js';
import { isOnline, onConnectivityChange } from '../net.js';
import { OfflineQueueStore, type QueuedScan } from '../offlineQueue.js';
import { signal, type FeedbackKind } from '../feedback.js';
import { openStockCheckSheet } from './stockLookup.js';
import { CORRECTION_REASONS } from '../types.js';
import type { ReceivingItem, ReceivingSession, ScanOutcome, CorrectionReason } from '../types.js';

// Camera-based barcode scanning (Suri: "can we use mobile camera for EAN
// scans", added 2026-09-30 alongside the existing hardware scanner).
// Deliberately NOT built on the native Shape Detection API
// (`BarcodeDetector`) — that only ships in Chrome on Android/ChromeOS,
// not desktop Chrome on Windows/Mac/Linux and not Safari anywhere
// (confirmed the hard way: Suri tried it in desktop Chrome and Safari
// and got "not supported" in both). ZXing decodes frames itself via a
// <canvas>, so it works anywhere `getUserMedia` does — desktop and
// mobile, Chrome/Safari/Firefox alike — which is the actual bar for
// Suri being able to test this from her own machine, not just from an
// Android warehouse device.
const CAMERA_SCAN_HINTS = new Map<DecodeHintType, unknown>([
  [
    DecodeHintType.POSSIBLE_FORMATS,
    [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.CODE_128],
  ],
]);

const DEVICE_ID_KEY = 'wr.deviceId';

function deviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export async function renderScan(container: HTMLElement, sessionId: string): Promise<void> {
  const div = document.createElement('div');
  div.className = 'screen';
  container.appendChild(div);
  div.innerHTML = '<p class="hint center">Loading session…</p>';

  let session: ReceivingSession;
  let items = new Map<string, ReceivingItem>();
  const offlineStore = new OfflineQueueStore();
  let stopConnectivityWatch: (() => void) | null = null;
  let flushTimer: ReturnType<typeof setInterval> | null = null;
  let destroyed = false;

  try {
    const state = await api.getSession(sessionId);
    session = state.session;
    items = new Map(state.items.map((i) => [i.id, i]));
  } catch (err) {
    div.innerHTML = `<div class="card"><p class="error-text">${escapeHtml(describeError(err))}</p></div><button class="ghost" id="back">ASN List</button>`;
    qs<HTMLButtonElement>(div, '#back').addEventListener('click', () => {
      location.hash = '#/asns';
    });
    return;
  }

  const locked = session.status === 'CONFIRMED' || session.status === 'COMPLETED';

  div.innerHTML = `
    <div class="topbar">
      <h1>${escapeHtml(session.poNumber)}</h1>
      <span class="status-pill" id="connPill"></span>
    </div>
    ${locked ? '<div class="card"><p><strong>This session is confirmed.</strong> It\'s read-only — scanning and corrections are closed. See the review screen for the final numbers.</p></div>' : ''}
    <div class="asn-progress" id="asnProgress"></div>
    <div class="last-scan" id="lastScan"><div class="sku">Ready</div><div class="count">Scan a barcode to begin</div></div>
    ${
      locked
        ? ''
        : `<div class="scan-input-wrap">
      <input type="text" id="scanInput" placeholder="Scan or type barcode" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" />
      <button type="button" class="ghost camera-btn" id="cameraBtn" aria-label="Scan with camera">📷</button>
    </div>`
    }
    <button class="ghost big" id="stockCheckBtn" style="margin:0.5rem 0;">Stock Check (look up stock &amp; location)</button>
    <div class="list" id="items"></div>
    <div class="footer-actions">
      <button class="ghost big" id="backBtn">ASN List</button>
      <button class="primary big" id="reviewBtn">${locked ? 'View Review' : 'Review & Confirm'}</button>
    </div>
  `;

  const lastScanEl = qs<HTMLDivElement>(div, '#lastScan');
  const itemsEl = qs<HTMLDivElement>(div, '#items');
  const connPill = qs<HTMLSpanElement>(div, '#connPill');
  const scanInput = div.querySelector<HTMLInputElement>('#scanInput');
  const cameraBtn = div.querySelector<HTMLButtonElement>('#cameraBtn');
  let stopCameraScan: (() => void) | null = null;

  const progressEl = qs<HTMLDivElement>(div, '#asnProgress');
  qs<HTMLButtonElement>(div, '#stockCheckBtn').addEventListener('click', () => openStockCheckSheet(() => focusInput()));
  qs<HTMLButtonElement>(div, '#backBtn').addEventListener('click', () => {
    location.hash = '#/asns';
  });
  qs<HTMLButtonElement>(div, '#reviewBtn').addEventListener('click', () => {
    location.hash = `#/review/${sessionId}`;
  });

  function renderConnPill(): void {
    const pending = offlineStore.count(sessionId);
    if (!isOnline()) {
      connPill.className = 'status-pill offline';
      connPill.textContent = pending > 0 ? `Offline · ${pending} queued` : 'Offline';
    } else if (pending > 0) {
      connPill.className = 'status-pill pending';
      connPill.textContent = `Syncing ${pending}…`;
    } else {
      connPill.className = 'status-pill';
      connPill.innerHTML = '<span class="dot"></span> Online';
    }
  }

  /** Whole-ASN progress: units received vs expected, plus how many SKU lines are complete / short / over. */
  function renderProgress(): void {
    const all = [...items.values()];
    const expected = all.reduce((n, i) => n + i.expectedQuantity, 0);
    const received = all.reduce((n, i) => n + i.receivedQuantity, 0);
    const pct = expected > 0 ? Math.min(100, Math.round((received / expected) * 100)) : 0;
    const done = all.filter((i) => i.expectedQuantity > 0 && i.receivedQuantity === i.expectedQuantity).length;
    const over = all.filter((i) => i.receivedQuantity > i.expectedQuantity).length;
    progressEl.innerHTML = `
      <div class="asn-progress-top"><strong>${received} / ${expected}</strong> units · ${pct}%</div>
      <div class="asn-bar${pct >= 100 ? ' full' : ''}"><div style="width:${pct}%"></div></div>
      <div class="hint" style="margin:0.25rem 0 0;">${done} of ${all.length} lines complete${over > 0 ? ` · <span class="error-text">${over} over</span>` : ''}</div>`;
  }

  function renderItems(): void {
    renderProgress();
    itemsEl.innerHTML = '';
    const sorted = [...items.values()].sort((a, b) => a.sku.localeCompare(b.sku));
    for (const item of sorted) {
      const row = document.createElement('button');
      row.className = 'item-row';
      row.dataset.itemId = item.id;
      row.style.textAlign = 'left';
      const statusCls = item.status.toLowerCase();
      row.innerHTML = `
        <div>
          <div class="sku">${escapeHtml(item.sku)}</div>
          <div class="ean">${escapeHtml(item.ean || 'no barcode')}</div>
          <span class="badge ${statusCls}">${item.status}</span>
        </div>
        <div class="qty">${item.receivedQuantity}<span class="expected"> / ${item.expectedQuantity}</span>${item.expectedQuantity > item.receivedQuantity ? `<div class="expected" style="font-size:0.8rem;">${item.expectedQuantity - item.receivedQuantity} to go</div>` : item.receivedQuantity > item.expectedQuantity ? `<div class="error-text" style="font-size:0.8rem;">${item.receivedQuantity - item.expectedQuantity} over</div>` : ''}</div>
      `;
      if (!locked) {
        row.addEventListener('click', () => openItemSheet(item.id));
      }
      itemsEl.appendChild(row);
    }
  }

  function flashLastScan(kind: FeedbackKind, title: string, subtitle: string): void {
    lastScanEl.classList.remove('flash-good', 'flash-bad', 'result-good', 'result-warn', 'result-bad');
    // Force reflow so the animation re-triggers on repeated identical scans.
    void lastScanEl.offsetWidth;
    if (kind !== 'neutral') lastScanEl.classList.add(`result-${kind}`);
    lastScanEl.innerHTML = `<div class="sku">${escapeHtml(title)}</div><div class="count">${escapeHtml(subtitle)}</div>`;
    signal(kind);
  }

  function focusInput(): void {
    if (!scanInput || destroyed) return;
    scanInput.focus({ preventScroll: true });
  }

  async function tryFlush(): Promise<void> {
    if (offlineStore.count(sessionId) === 0) return;
    const result = await offlineStore.flush(sessionId, async (scan: QueuedScan) => {
      try {
        await api.scan(sessionId, { ean: scan.ean, deviceId: scan.deviceId, correlationId: scan.correlationId });
        return { networkError: false, ok: true };
      } catch (err) {
        if (err instanceof ApiNetworkError) return { networkError: true, ok: false };
        return { networkError: false, ok: false };
      }
    });
    renderConnPill();
    if (result.synced > 0 || result.failed > 0) {
      // Queued scans applied server-side without this device tracking
      // their resulting counts locally — refresh from the source of
      // truth rather than guess.
      try {
        const state = await api.getSession(sessionId);
        session = state.session;
        items = new Map(state.items.map((i) => [i.id, i]));
        renderItems();
      } catch {
        // Best-effort refresh — the next successful load will catch up.
      }
    }
  }

  async function submitScan(ean: string): Promise<void> {
    await tryFlush();
    const correlationId = newCorrelationId();
    if (offlineStore.count(sessionId) > 0 || !isOnline()) {
      offlineStore.add({ correlationId, sessionId, ean, deviceId: deviceId(), queuedAt: new Date().toISOString() });
      renderConnPill();
      flashLastScan('neutral', ean, 'Queued — will sync when back online');
      return;
    }
    try {
      const outcome = await api.scan(sessionId, { ean, deviceId: deviceId(), correlationId });
      handleOutcome(ean, outcome);
    } catch (err) {
      if (err instanceof ApiNetworkError) {
        offlineStore.add({ correlationId, sessionId, ean, deviceId: deviceId(), queuedAt: new Date().toISOString() });
        renderConnPill();
        flashLastScan('neutral', ean, 'Queued — will sync when back online');
      } else {
        flashLastScan('bad', 'Error', describeError(err));
      }
    }
  }

  function handleOutcome(ean: string, outcome: ScanOutcome): void {
    if (outcome.kind === 'SCANNED') {
      items.set(outcome.item.id, outcome.item);
      renderItems();
      const { receivedQuantity: got, expectedQuantity: want } = outcome.item;
      if (got > want) {
        flashLastScan('bad', `${outcome.item.sku} — OVER by ${got - want}`, `${got} received, only ${want} expected`);
      } else if (got === want) {
        flashLastScan('good', `✓ ${outcome.item.sku} complete`, `${got} / ${want} received`);
      } else {
        flashLastScan('good', outcome.item.sku, `${got} / ${want} · ${want - got} to go`);
      }
    } else if (outcome.kind === 'UNKNOWN_BARCODE') {
      flashLastScan('bad', 'Unknown barcode', ean);
      openUnknownBarcodeModal(ean);
    } else {
      flashLastScan('warn', 'Already recorded', ean);
    }
  }

  if (scanInput) {
    scanInput.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const value = scanInput.value.trim();
      scanInput.value = '';
      if (!value) return;
      void submitScan(value);
    });
    scanInput.addEventListener('blur', () => {
      // Re-focus shortly after losing focus (e.g. a stray tap outside any
      // interactive element) so the hardware scanner's keyboard-emulated
      // input keeps landing here without the operator having to tap back
      // in — but not immediately, so buttons/links still receive their
      // click first.
      setTimeout(() => {
        if (!document.querySelector('.modal-backdrop')) focusInput();
      }, 250);
    });
    focusInput();
  }

  if (cameraBtn) {
    cameraBtn.addEventListener('click', () => openCameraScanModal());
  }

  function openCameraScanModal(): void {
    if (!navigator.mediaDevices?.getUserMedia) {
      const backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop';
      backdrop.innerHTML = `
        <div class="modal">
          <h2>Camera scanning unavailable</h2>
          <p>This browser doesn't support camera access. Keep using the handheld scanner or type the barcode in.</p>
          <button class="ghost" id="closeBtn">Close</button>
        </div>
      `;
      document.body.appendChild(backdrop);
      const close = () => {
        backdrop.remove();
        focusInput();
      };
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) close();
      });
      qs<HTMLButtonElement>(backdrop, '#closeBtn').addEventListener('click', close);
      return;
    }

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `
      <div class="modal camera-modal">
        <h2>Scan with Camera</h2>
        <p class="hint" id="cameraStatus">Starting camera…</p>
        <video id="cameraVideo" autoplay playsinline muted></video>
        <button class="ghost" id="cancelCamera" style="margin-top:0.75rem;">Cancel</button>
      </div>
    `;
    document.body.appendChild(backdrop);

    const video = qs<HTMLVideoElement>(backdrop, '#cameraVideo');
    const statusEl = qs<HTMLParagraphElement>(backdrop, '#cameraStatus');
    let controls: { stop: () => void } | null = null;
    let stopped = false;

    const stop = () => {
      if (stopped) return;
      stopped = true;
      controls?.stop();
      stopCameraScan = null;
    };

    const close = () => {
      stop();
      backdrop.remove();
      focusInput();
    };
    stopCameraScan = close;

    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#cancelCamera').addEventListener('click', close);

    const reader = new BrowserMultiFormatReader(CAMERA_SCAN_HINTS);
    let handled = false;

    void (async () => {
      try {
        controls = await reader.decodeFromConstraints(
          { video: { facingMode: 'environment' } },
          video,
          (result, err) => {
            if (stopped || handled) return;
            if (result) {
              handled = true;
              const value = result.getText();
              close();
              void submitScan(value);
              return;
            }
            // `err` fires on every frame with no barcode found yet —
            // not a real failure, just "keep looking" — so it's not
            // surfaced. A genuine failure to even start the camera
            // (permission denied, no camera, HTTPS required) throws
            // out of decodeFromConstraints itself instead, caught below.
            void err;
          },
        );
        if (stopped) {
          // Modal was closed while permission was still pending.
          controls.stop();
          return;
        }
        statusEl.textContent = 'Point the camera at a barcode.';
      } catch {
        statusEl.classList.add('error-text');
        statusEl.textContent = "Couldn't access the camera. Check permissions and try again.";
      }
    })();
  }

  function openItemSheet(itemId: string): void {
    const item = items.get(itemId);
    if (!item) return;
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `
      <div class="modal">
        <h2>${escapeHtml(item.sku)}</h2>
        <p class="hint">${escapeHtml(item.ean || 'no barcode')} · Received ${item.receivedQuantity} / Expected ${item.expectedQuantity}</p>
        <div class="actions-row">
          <button class="ghost" id="undoBtn">Undo Last Scan</button>
          <button class="primary" id="editBtn">Edit Quantity</button>
        </div>
        <p id="sheetStatus" class="hint"></p>
        <button class="ghost" id="closeBtn" style="margin-top:0.5rem;">Close</button>
      </div>
    `;
    document.body.appendChild(backdrop);
    const close = () => {
      backdrop.remove();
      focusInput();
    };
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#closeBtn').addEventListener('click', close);
    qs<HTMLButtonElement>(backdrop, '#undoBtn').addEventListener('click', async () => {
      const status = qs<HTMLParagraphElement>(backdrop, '#sheetStatus');
      status.textContent = 'Undoing…';
      try {
        const outcome = await api.undo(sessionId, { itemId, deviceId: deviceId() });
        if (outcome.kind === 'UNDONE') {
          items.set(outcome.item.id, outcome.item);
          renderItems();
          close();
        } else {
          status.textContent = 'Nothing to undo for this item.';
        }
      } catch (err) {
        status.classList.add('error-text');
        status.textContent = describeError(err);
      }
    });
    qs<HTMLButtonElement>(backdrop, '#editBtn').addEventListener('click', () => {
      backdrop.remove();
      openEditModal(itemId);
    });
  }

  function openEditModal(itemId: string): void {
    const item = items.get(itemId);
    if (!item) return;
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `
      <div class="modal">
        <h2>Edit Quantity — ${escapeHtml(item.sku)}</h2>
        <label for="newQty">New final quantity</label>
        <input type="number" id="newQty" min="0" step="1" value="${item.receivedQuantity}" />
        <label for="reason">Reason</label>
        <select id="reason" style="width:100%; min-height:56px; background:var(--surface); color:var(--text); border:2px solid var(--border); border-radius:14px; padding:0 1rem;">
          <option value="">Select a reason…</option>
          ${CORRECTION_REASONS.map((r) => `<option value="${r}">${labelFor(r)}</option>`).join('')}
        </select>
        <label for="notes">Notes ${''}</label>
        <input type="text" id="notes" placeholder="Required if reason is Other" />
        <p id="editStatus" class="hint"></p>
        <div class="actions-row">
          <button class="ghost" id="cancelEdit">Cancel</button>
          <button class="primary" id="submitEdit">Save</button>
        </div>
      </div>
    `;
    document.body.appendChild(backdrop);
    const close = () => {
      backdrop.remove();
      focusInput();
    };
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#cancelEdit').addEventListener('click', close);

    async function submit(confirmedLargeChange?: boolean): Promise<void> {
      const status = qs<HTMLParagraphElement>(backdrop, '#editStatus');
      const qtyInput = qs<HTMLInputElement>(backdrop, '#newQty');
      const reasonSelect = qs<HTMLSelectElement>(backdrop, '#reason');
      const notesInput = qs<HTMLInputElement>(backdrop, '#notes');
      const newQuantity = Number(qtyInput.value);
      status.classList.remove('error-text');
      status.textContent = 'Saving…';
      try {
        const outcome = await api.setQuantity(sessionId, itemId, {
          newQuantity,
          correctionReason: (reasonSelect.value || undefined) as CorrectionReason | undefined,
          correctionNotes: notesInput.value || undefined,
          confirmedLargeChange,
          deviceId: deviceId(),
        });
        if (outcome.kind === 'APPLIED') {
          items.set(outcome.item.id, outcome.item);
          renderItems();
          close();
        } else if (outcome.kind === 'REQUIRES_LARGE_CHANGE_CONFIRMATION') {
          const ok = confirm(`This changes the quantity by ${outcome.delta > 0 ? '+' : ''}${outcome.delta}, which is a large change. Continue?`);
          if (ok) await submit(true);
          else status.textContent = '';
        } else {
          status.classList.add('error-text');
          status.textContent = outcome.reason;
        }
      } catch (err) {
        status.classList.add('error-text');
        status.textContent = describeError(err);
      }
    }

    qs<HTMLButtonElement>(backdrop, '#submitEdit').addEventListener('click', () => void submit());
  }

  function openUnknownBarcodeModal(ean: string): void {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `
      <div class="modal">
        <h2>Unknown barcode</h2>
        <p>${escapeHtml(ean)} isn't on this ASN's expected item list. It won't be received against anything unless you record it as an exception for review.</p>
        <p id="uStatus" class="hint"></p>
        <div class="actions-row">
          <button class="ghost" id="dismiss">Dismiss</button>
          <button class="primary" id="recordException">Record Exception</button>
        </div>
      </div>
    `;
    document.body.appendChild(backdrop);
    const close = () => {
      backdrop.remove();
      focusInput();
    };
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#dismiss').addEventListener('click', close);
    qs<HTMLButtonElement>(backdrop, '#recordException').addEventListener('click', async () => {
      const status = qs<HTMLParagraphElement>(backdrop, '#uStatus');
      status.textContent = 'Recording…';
      try {
        await api.recordException(sessionId, { ean, sku: null, type: 'UNKNOWN_BARCODE' });
        close();
      } catch (err) {
        status.classList.add('error-text');
        status.textContent = describeError(err);
      }
    });
  }

  renderItems();
  renderConnPill();
  stopConnectivityWatch = onConnectivityChange(() => {
    renderConnPill();
    void tryFlush();
  });
  if (!locked) {
    flushTimer = setInterval(() => void tryFlush(), 8000);
  }

  // Cleanup when navigating away — nothing in this screen holds a
  // reference the router keeps around, but the interval/listener would
  // otherwise keep firing (and keep a closure over stale `items`) after
  // the DOM node is discarded.
  const cleanup = () => {
    destroyed = true;
    if (flushTimer) clearInterval(flushTimer);
    stopConnectivityWatch?.();
    stopCameraScan?.();
    window.removeEventListener('hashchange', cleanup);
  };
  window.addEventListener('hashchange', cleanup, { once: true });
}

function labelFor(reason: CorrectionReason): string {
  return reason
    .toLowerCase()
    .split('_')
    .map((w) => w[0]!.toUpperCase() + w.slice(1))
    .join(' ');
}

function describeError(err: unknown): string {
  if (err instanceof ApiNetworkError) return 'Network error.';
  if (err instanceof ApiError) return `${err.message}${err.code ? ` (${err.code})` : ''}`;
  return String(err);
}
